import type { Draft, Surface } from '../shared/types';
import { BLOCK_WIDTH, SCENE_HEIGHT } from '../shared/sceneDimensions';

export const DEFAULT_ZOOM=1;
export const MIN_ZOOM=1;
export const MAX_ZOOM=6;
export const BROWSE_ZOOM_KEY='graffiti:browse-zoom:v1';
export interface BrowseZoomStorage {getItem:(key:string)=>string|null;setItem:(key:string,value:string)=>void}
function deviceStorage():BrowseZoomStorage|undefined {try{return globalThis.localStorage;}catch{return undefined;}}
/** Only the browse pose is saved; panel editors have a separate view. */
export function readBrowseZoom(storage:BrowseZoomStorage|undefined=deviceStorage()):number {
  try {const raw=storage?.getItem(BROWSE_ZOOM_KEY);if(raw===undefined||raw===null)return DEFAULT_ZOOM;const value:unknown=JSON.parse(raw);return typeof value==='number'&&Number.isFinite(value)?Math.max(MIN_ZOOM,Math.min(MAX_ZOOM,value)):DEFAULT_ZOOM;}
  catch{return DEFAULT_ZOOM;}
}
export function saveBrowseZoom(zoom:number,storage:BrowseZoomStorage|undefined=deviceStorage()):void {
  if(!Number.isFinite(zoom))return;
  try {storage?.setItem(BROWSE_ZOOM_KEY,String(Math.max(MIN_ZOOM,Math.min(MAX_ZOOM,zoom))));}catch{/* Browsing also works when device storage is unavailable. */}
}
export interface CameraPose { x:number; y:number; zoom:number }
// Baseline inset fixes the scale; live occlusion only bounds deliberate navigation.
export interface CameraViewport { width:number; height:number; bottomInset?:number; occludedBottom?:number }
export interface CameraAnchor { x:number; y:number }

export function cameraSize(zoom:number,aspect:number,bottomInset=0,maxZoom=MAX_ZOOM,minZoom=MIN_ZOOM):CameraViewport {
  const height=SCENE_HEIGHT/Math.max(minZoom,Math.min(maxZoom,zoom))/(1-Math.max(0,Math.min(.95,bottomInset)));
  return {width:height*aspect,height};
}
/** Carry the actual rendered panel viewport into freestyle without reframing.
 * A close panel can exceed ordinary browse zoom; preserve that inherited scale
 * until deliberate zooming reduces it, without raising normal browse limits. */
export function cameraFromRenderedView(view:{x:number;y:number},height:number,bottomInset=0):CameraPose {
  return {...view,zoom:SCENE_HEIGHT/(height*(1-Math.max(0,Math.min(.95,bottomInset))))};
}
export function defaultCameraY(zoom=DEFAULT_ZOOM):number {return (SCENE_HEIGHT-cameraSize(zoom,1).height)*.65;}
export function clampPose(pose:CameraPose,aspect:number,worldWidth:number,bottomInset=0,occludedBottom=bottomInset,maxZoom=MAX_ZOOM,minZoom=MIN_ZOOM):CameraPose {
  const zoom=Math.max(minZoom,Math.min(maxZoom,pose.zoom));
  const size=cameraSize(zoom,aspect,bottomInset,maxZoom,minZoom);
  return {x:clampCamera(pose.x,Math.max(0,worldWidth-size.width)),y:clampCamera(pose.y,Math.max(0,SCENE_HEIGHT-size.height*(1-Math.max(0,Math.min(.95,occludedBottom))))),zoom};
}
/** Street/map visits retain the player's browse zoom and vertical position. */
export function visitBlockCamera(pose:CameraPose,blockId:number,aspect:number,worldWidth:number,blockStart=blockId*BLOCK_WIDTH,bottomInset=0,occludedBottom=bottomInset):CameraPose {
  const size=cameraSize(pose.zoom,aspect,bottomInset);
  return clampPose({...pose,x:blockStart+500-size.width/2},aspect,worldWidth,bottomInset,occludedBottom);
}
// Anchor fractions belong to the viewport. Keeping the same world point under
// the moving pinch midpoint avoids camera feedback and finger drift.
export function zoomCamera(pose:CameraPose,zoom:number,viewport:CameraViewport,worldWidth:number,anchor:CameraAnchor={x:.5,y:.5},current:CameraAnchor=anchor):CameraPose {
  const aspect=viewport.width/Math.max(1,viewport.height);
  const maxZoom=Math.max(MAX_ZOOM,pose.zoom),minZoom=Math.min(MIN_ZOOM,pose.zoom);
  const before=cameraSize(pose.zoom,aspect,viewport.bottomInset,maxZoom,minZoom),after=cameraSize(zoom,aspect,viewport.bottomInset,maxZoom,minZoom);
  return clampPose({x:pose.x+anchor.x*before.width-current.x*after.width,y:pose.y+anchor.y*before.height-current.y*after.height,zoom},aspect,worldWidth,viewport.bottomInset,viewport.occludedBottom,maxZoom,minZoom);
}
export function panCamera(pose:CameraPose,dx:number,dy:number,viewport:CameraViewport,worldWidth:number):CameraPose {
  const aspect=viewport.width/Math.max(1,viewport.height),size=cameraSize(pose.zoom,aspect,viewport.bottomInset);
  return clampPose({x:pose.x-dx*size.width/Math.max(1,viewport.width),y:pose.y-dy*size.height/Math.max(1,viewport.height),zoom:pose.zoom},aspect,worldWidth,viewport.bottomInset,viewport.occludedBottom);
}
// Browse swipes move one screen pixel per finger pixel, regardless of zoom.
// Unlike two-finger navigation, they deliberately preserve the vertical pose.
export function panStreetCamera(pose:CameraPose,dx:number,viewport:CameraViewport,worldWidth:number):CameraPose {
  const size=cameraSize(pose.zoom,viewport.width/Math.max(1,viewport.height),viewport.bottomInset,Math.max(MAX_ZOOM,pose.zoom),Math.min(MIN_ZOOM,pose.zoom));
  return {...pose,x:clampCamera(pose.x-dx*size.width/Math.max(1,viewport.width),Math.max(0,worldWidth-size.width))};
}
export interface SwipeSample { x:number; time:number }
export function swipeVelocity(samples:SwipeSample[],releasedAt:number):number {
  const recent=samples.filter(sample=>releasedAt-sample.time<=100);
  const first=recent[0],last=recent.at(-1);
  if(!first||!last||releasedAt-last.time>70||last.time-first.time<8)return 0;
  return Math.max(-4,Math.min(4,(last.x-first.x)/(last.time-first.time)));
}
export interface StreetGlide { pose:CameraPose; velocity:number; elapsed:number }
// Velocity is camera motion in screen pixels/ms. Integrating exponential decay
// keeps the glide consistent across frame rates; a delayed frame is bounded.
export function advanceStreetGlide(glide:StreetGlide,delta:number,viewport:CameraViewport,worldWidth:number):StreetGlide {
  const dt=Math.max(0,Math.min(64,delta,1200-glide.elapsed)),decay=Math.exp(-dt/260);
  if(!dt)return glide.elapsed>=1200?{...glide,velocity:0}:glide;
  const screenTravel=glide.velocity*260*(1-decay);
  const size=cameraSize(glide.pose.zoom,viewport.width/Math.max(1,viewport.height),viewport.bottomInset,Math.max(MAX_ZOOM,glide.pose.zoom),Math.min(MIN_ZOOM,glide.pose.zoom));
  const intendedX=glide.pose.x+screenTravel*size.width/Math.max(1,viewport.width);
  const pose=panStreetCamera(glide.pose,-screenTravel,viewport,worldWidth);
  const velocity=Math.abs(pose.x-intendedX)>1e-8||glide.elapsed+dt>=1200||Math.abs(glide.velocity*decay)<.025?0:glide.velocity*decay;
  return {pose,velocity,elapsed:glide.elapsed+dt};
}
interface GlideClock {now:()=>number;request:(tick:(time:number)=>void)=>number;cancel:(id:number)=>void}
export interface StreetGlideRun {cancel:()=>void;pose:()=>CameraPose}
export function startStreetGlide(initial:StreetGlide,viewport:CameraViewport,worldWidth:()=>number,publish:(pose:CameraPose)=>void,clock:GlideClock={now:()=>performance.now(),request:tick=>requestAnimationFrame(tick),cancel:id=>cancelAnimationFrame(id)}):StreetGlideRun {
  let glide=initial,previous=clock.now(),frame:number|null=null,stopped=false;
  const tick=(time:number)=>{
    frame=null;
    if(stopped)return;
    glide=advanceStreetGlide(glide,time-previous,viewport,worldWidth());previous=time;
    publish(glide.pose);
    if(!stopped&&glide.velocity)frame=clock.request(tick);
  };
  frame=clock.request(tick);
  return {pose:()=>glide.pose,cancel:()=>{stopped=true;if(frame!==null)clock.cancel(frame);frame=null;}};
}
export function frameDraftY(surface:Surface,draft:Draft,height:number):number {
  return clampCamera(surface.y+draft.y+draft.height/2-height/2,Math.max(0,SCENE_HEIGHT-height));
}
export function frameDraftCamera(surface:Surface,draft:Draft,aspect:number,worldWidth:number,zoom:number,bounds:Pick<Draft,'width'|'height'>=draft,blockStart=surface.blockId*BLOCK_WIDTH):CameraPose {
  const fittedZoom=Math.max(MIN_ZOOM,Math.min(zoom,SCENE_HEIGHT/(bounds.height+48),SCENE_HEIGHT*aspect/(bounds.width+48)));
  const size=cameraSize(fittedZoom,aspect);
  return {x:frameDraft(surface,draft,size.width,Math.max(0,worldWidth-size.width),blockStart),y:frameDraftY(surface,draft,size.height),zoom:fittedZoom};
}

export function clampCamera(x:number, maximum:number):number {
  return Math.max(0,Math.min(maximum,x));
}

export function frameDraft(surface:Surface, draft:Draft, viewportWidth:number, maximum:number,blockStart=surface.blockId*BLOCK_WIDTH):number {
  const center=blockStart+surface.x+draft.x+draft.width/2;
  return clampCamera(center-viewportWidth/2,maximum);
}
